import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RcFile } from 'antd/lib/upload';
import { useUpdateEffect } from 'usehooks-ts';

import './index.less';

import {
  ASRProviderMediaGroup,
  ASRProviderReferencePolicyStatus,
  ASRProviderReferenceSuiteOutput,
  ASRProviderReferenceSuiteStatus,
  FileSourceParametersSettings,
  IngestionPipelineOutput,
  IngestionSourceParametersOutput,
  OCRProviderMediaGroup,
  OCRProviderReferencePolicyStatus,
  OCRProviderReferenceSuiteOutput,
  OCRProviderReferenceSuiteStatus,
} from '../../../../../../gamma-api';
import IbTypography from '../../../../../components/common/IbTypography';
import IbInput from '../../../../../components/common/IbInput';
import FileUpload from '../../../../../components/common/FileUpload';
import { formatFileCount } from '../../../../../utils/fileUtil';
import FileTable, { FileTableStore, IFileTableItem } from '../FileTable';
import { FormAPI } from '../../../../../types';
import IbButton from '../../../../../components/common/IbButton';
import IbIcon from '../../../../../components/common/IbIcon';
import { FileExtensions } from '../../../../../constants';
import {
  getSourceAsrOverlays,
  getSourceAsrSettings,
  getSourceOcrOverlays,
  getSourceOcrSettings,
  mapSourceAsrSettings,
  mapSourceOcrSettings,
} from '../SourceModal/utils';
import RecognizerSelector from '../../../../../components/RecognizerSelector';

import { BLOCK_GROUP_CLASS_NAME, BLOCK_ITEM_CLASS_NAME, FILE_UPLOAD_BUTTON_CLASS_NAME, MAIN_CLASS_NAME } from './const';
import { useValidationHandle } from './useValidationHandle';

const INSIGNIFICANT_FILE_EXTENSIONS = [
  FileExtensions.HTM,
  FileExtensions.XHTML,
  FileExtensions.XHT,
  FileExtensions.YML,
  FileExtensions.JFIF,
  FileExtensions.JPG,
  FileExtensions.JPE,
  FileExtensions.TIF,
  FileExtensions.DIB,
];

export interface ISourceSettingsFileProps {
  loading: boolean;
  pipeline?: IngestionPipelineOutput;
  parameters: IngestionSourceParametersOutput;
  files: IFileTableItem[];
  onChange?: (parameters: IngestionSourceParametersOutput) => void;
  onFileCreate?: (files: RcFile[], store: FileTableStore) => Promise<void>;
  onFileDelete?: (files: IFileTableItem[], store: FileTableStore) => Promise<void>;
}

const getFileExtensions = (
  parameters?: IngestionSourceParametersOutput,
  pipeline?: IngestionPipelineOutput
): FileExtensions[] => {
  const result = [
    FileExtensions.TXT,
    FileExtensions.MD,
    FileExtensions.HTML,
    FileExtensions.HTM,
    FileExtensions.XHTML,
    FileExtensions.XHT,
    FileExtensions.XML,
    FileExtensions.JSON,
    FileExtensions.YAML,
    FileExtensions.YML,
    FileExtensions.CSV,
    FileExtensions.DOCX,
    FileExtensions.XLSX,
    FileExtensions.XLS,
  ];

  const ocr = getSourceOcrSettings(pipeline, parameters?.extraction);
  if (ocr?.status === OCRProviderReferenceSuiteStatus.Enabled) {
    for (const policy of ocr.policies) {
      if (policy.status !== OCRProviderReferencePolicyStatus.Enabled || !policy.provider_id) {
        continue;
      }

      if (policy.media_groups.includes(OCRProviderMediaGroup.Document)) {
        result.push(FileExtensions.PDF);
      }

      if (policy.media_groups.includes(OCRProviderMediaGroup.Image)) {
        result.push(FileExtensions.JPEG);
        result.push(FileExtensions.JFIF);
        result.push(FileExtensions.JPG);
        result.push(FileExtensions.JPE);
        result.push(FileExtensions.PNG);
        result.push(FileExtensions.GIF);
        result.push(FileExtensions.WEBP);
        result.push(FileExtensions.TIFF);
        result.push(FileExtensions.TIF);
        result.push(FileExtensions.BMP);
        result.push(FileExtensions.DIB);
      }
    }
  }

  const asr = getSourceAsrSettings(pipeline, parameters?.extraction);
  if (asr?.status === ASRProviderReferenceSuiteStatus.Enabled) {
    for (const policy of asr.policies) {
      if (policy.status !== ASRProviderReferencePolicyStatus.Enabled || !policy.provider_id) {
        continue;
      }

      if (policy.media_groups.includes(ASRProviderMediaGroup.Audio)) {
        result.push(FileExtensions.MP3);
        result.push(FileExtensions.WAV);
        result.push(FileExtensions.FLAC);
        result.push(FileExtensions.M4A);
        result.push(FileExtensions.OGG);
        result.push(FileExtensions.AAC);
      }
    }
  }

  return result;
};

const SourceSettingsFile = forwardRef<FormAPI, ISourceSettingsFileProps>(
  ({ loading, pipeline, parameters, files, onChange, onFileCreate, onFileDelete }, ref) => {
    const { t } = useTranslation();

    const [errors, setErrors] = useValidationHandle(
      ref,
      (isTouched: boolean) => {
        return {
          name: { isTouched: isTouched, isError: !parameters.name },
          files: { isTouched: isTouched, isError: !files.length },
        };
      },
      [parameters.name, files]
    );

    useUpdateEffect(() => {
      setErrors({ ...errors, files: { isError: !files.length, isTouched: true } });
    }, [files]);

    const onNameChange = (value: string) => {
      onChange?.({
        ...parameters,
        name: value,
      });

      setErrors({ ...errors, name: { isError: !value, isTouched: true } });
    };

    const onNameBlur = () => setErrors({ ...errors, name: { isError: errors.name.isError, isTouched: true } });

    const onOcrChange = (ocr: OCRProviderReferenceSuiteOutput) => {
      onChange?.({
        ...parameters,
        extraction: {
          ...parameters.extraction,
          ocr: mapSourceOcrSettings(parameters.extraction.ocr, ocr),
        },
      });
    };

    const onAsrChange = (asr: ASRProviderReferenceSuiteOutput) => {
      onChange?.({
        ...parameters,
        extraction: {
          ...parameters.extraction,
          asr: mapSourceAsrSettings(parameters.extraction.asr, asr),
        },
      });
    };

    const onFileUpload = async (multipleFiles: RcFile[]) => {
      if (!onFileCreate) {
        return false;
      }

      if (!parameters.name) {
        onChange?.({
          ...parameters,
          name: multipleFiles[0].name,
        });
      }

      const store = FileTableStore.Server;
      await onFileCreate(multipleFiles, store);

      return false;
    };

    const onDeleteClick = async (singleFile: IFileTableItem) => {
      if (!onFileDelete) {
        return;
      }

      const settings = parameters.settings as FileSourceParametersSettings;
      const store =
        singleFile.server && settings.files?.some((file) => file.file_id === singleFile.server?.file_id)
          ? FileTableStore.Client
          : FileTableStore.Server;
      await onFileDelete([singleFile], store);
    };

    const fileExtensions = getFileExtensions(parameters, pipeline);

    return (
      <div className={MAIN_CLASS_NAME}>
        <div className={BLOCK_GROUP_CLASS_NAME}>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Source settings: File: Name: Title')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">{t('Source settings: Common: Name: Hint')}</IbTypography.Paragraph>
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbInput
              status={errors.name.isTouched && errors.name.isError ? 'error' : 'default'}
              value={parameters.name}
              onBlur={onNameBlur}
              onChange={onNameChange}
            />
            {errors.name.isTouched && errors.name.isError && (
              <IbTypography.Paragraph error type="descriptor">
                {t('Source settings: File: Name: Error')}
              </IbTypography.Paragraph>
            )}
          </div>
        </div>
        <div className={BLOCK_GROUP_CLASS_NAME}>
          <RecognizerSelector.Ocr
            overlays={getSourceOcrOverlays(pipeline)}
            suite={getSourceOcrSettings(pipeline, parameters.extraction)}
            onChange={onOcrChange}
          />
        </div>
        <div className={BLOCK_GROUP_CLASS_NAME}>
          <RecognizerSelector.Asr
            overlays={getSourceAsrOverlays(pipeline)}
            suite={getSourceAsrSettings(pipeline, parameters.extraction)}
            onChange={onAsrChange}
          />
        </div>
        <div className={BLOCK_ITEM_CLASS_NAME}>
          <FileUpload multiple noButton accept={fileExtensions.join(',')} disabled={loading} onUpload={onFileUpload}>
            <div className={FILE_UPLOAD_BUTTON_CLASS_NAME}>
              <IbButton icon={<IbIcon iconName="add-one" />} type="link">
                {t('Source settings: File: Upload: Button: Title')}
              </IbButton>
              <IbTypography>
                <IbTypography.Paragraph disabled type="descriptor">
                  {t('Source settings: File: Upload: Button: Description: 1', {
                    types: fileExtensions
                      .filter((ext) => !INSIGNIFICANT_FILE_EXTENSIONS.includes(ext))
                      .map((ext) => ext.substring(1).toUpperCase())
                      .join(', '),
                  })}
                </IbTypography.Paragraph>
                <IbTypography.Paragraph disabled type="descriptor">
                  {t('Source settings: File: Upload: Button: Description: 2')}
                </IbTypography.Paragraph>
              </IbTypography>
            </div>
          </FileUpload>
          {errors.files.isTouched && errors.files.isError && (
            <IbTypography.Paragraph error type="descriptor">
              {t('Source settings: File: Files: Error')}
            </IbTypography.Paragraph>
          )}
        </div>
        {!!files.length && (
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Source settings: File: Files: Title', { value: loading ? '?' : formatFileCount(files.length) })}
            </IbTypography.Paragraph>
            <FileTable items={files} loading={loading} onDelete={onDeleteClick} />
          </div>
        )}
      </div>
    );
  }
);

export default SourceSettingsFile;
